import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';

import {
    Grid,
    TextField,
    Paper,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableCell,
    Button,
    TableRow,
    Typography,
} from '@mui/material';
import { Delete } from '@mui/icons-material';

import { GroupUserEditDialog } from './components';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';

import ScheduleGroupsQuery from '../../../services/query/scheduleGroup/ScheduleGroupQuery.services';
import ScheduleGroupCommand from '../../../services/command/scheduleGroupCommand/ScheduleGroupCommand.services';
import { GU } from './groupUser.classes';

export const GroupUser = () => {
    const [users, setUsers] = useState<any[]>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [userSearchText, setUserSearchText] = useState<string>('');
    const [isUserModalOpen, setIsUserModalOpen] = useState<boolean>(false);

    const { t } = useTranslation();

    const { groupId } = useParams<{ groupId: string }>();
    const history = useHistory();

    if (!groupId || groupId === 'undefined' || groupId === 'null') {
        history.push('/schedule-groups');
    }

    const onUserModalClose = () => {
        setIsUserModalOpen(false);
    };

    const getUsers = useCallback(() => {
        setIsLoading(true);
        ScheduleGroupsQuery.GetGroupUsers({
            groupId,
        })
            .then((res: any) => {
                if (res) {
                    setIsLoading(false);
                    setUsers(res.data);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    }, [groupId, t]);

    const removeUser = (scheduleGroupUserId: any) => {
        setIsLoading(true);
        ScheduleGroupCommand.DeleteGroupUser({
            scheduleGroupUserId,
        })
            .then((res: any) => {
                if (res) {
                    Swal.fire({
                        position: 'center',
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    setIsLoading(false);
                    getUsers();
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
                setIsLoading(false);
            });
    };

    const searchedUsers = useMemo(() => {
        if (!userSearchText) return users || [];

        return (users || []).filter((user: any) => user.username?.toLowerCase().includes(userSearchText.toLowerCase()));
    }, [userSearchText, users]);

    useEffect(() => {
        if (!groupId || groupId === 'undefined' || groupId === 'null') {
            return;
        }
        getUsers();
    }, [getUsers, groupId]);

    return (
        <>
            <GroupUserEditDialog
                open={isUserModalOpen}
                onClose={onUserModalClose}
                scheduleGroupId={groupId}
                setIsLoading={setIsLoading}
                getUsers={getUsers}
            />
            <SCUIContent className={GU.page}>
                <>
                    {isLoading && (
                        <div className={GU.loadingSection}>
                            <SCUILoadingLinear />
                        </div>
                    )}
                    <Grid className={GU.layout} container direction="row">
                        <Grid
                            className={GU.breadcrumbCol}
                            size={{
                                lg: 10,
                                md: 12,
                                xs: 12
                            }}>
                            <SCUIBreadCrumb
                                onlyTitle={t('scheduleGroup.groupUser.header')}
                                style={{ cursor: 'pointer' }}
                                components={[
                                    {
                                        title: t('scheduleGroup.header'),
                                        link: '/schedule-groups',
                                    },
                                ]}
                            />
                        </Grid>
                        <Grid
                            className={GU.addUserCol}
                            size={{
                                lg: 2,
                                md: 6,
                                xs: 6
                            }}>
                            <SCUIButton
                                className={GU.addUserButton}
                                text={t('scheduleGroup.groupUser.addUser')}
                                type="primary"
                                sizeType="fixed"
                                size={'m'}
                                onClick={() => setIsUserModalOpen(true)}
                            />
                        </Grid>
                        {!isLoading && (users?.length === 0 || !users) ? (
                            <Typography className={GU.emptyText} sx={{ fontWeight: 'bold', marginTop: '24px' }}>
                                {t('scheduleGroup.noData')}
                            </Typography>
                        ) : (
                            <>
                                <Grid
                                    className={GU.searchRow}
                                    sx={{ marginBottom: 1, marginTop: 1 }}
                                    size={{
                                        lg: 12,
                                        md: 12,
                                        xs: 12
                                    }}>
                                    <TextField
                                        className={GU.searchInput}
                                        value={userSearchText}
                                        onChange={(e: any) => setUserSearchText(e.target.value)}
                                        fullWidth
                                        placeholder={t('scheduleGroup.groupUser.searchUser')}
                                    />
                                </Grid>
                                <Paper className={GU.tableOuterPaper} sx={{ padding: '2rem', margin: '2rem', width: '100%' }}>
                                    <TableContainer className={GU.tableContainer} component={Paper}>
                                        <Table className={GU.table} aria-label="operators">
                                            <TableHead className={GU.tableHead}>
                                                <TableRow className={GU.tableHeadRow}>
                                                    <TableCell className={GU.tableHeadCell} align="center">
                                                        {t('scheduleGroup.groupUser.username')}
                                                    </TableCell>
                                                    <TableCell className={GU.tableHeadCell} align="center">
                                                        {t('scheduleGroup.groupUser.removeUser')}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody className={GU.tableBody}>
                                                {(searchedUsers || []).map((row: any) => {
                                                    return (
                                                        <TableRow
                                                            className={GU.tableRow}
                                                            sx={{ '& > *': { borderBottom: 'unset' } }}
                                                            key={row.scheduleGroupUserId}
                                                            data-schedule-group-user-id={row.scheduleGroupUserId}
                                                        >
                                                            <TableCell
                                                                className={[GU.tableCell, GU.tableCellUsername].join(' ')}
                                                                align="center"
                                                            >
                                                                {row.username}
                                                            </TableCell>
                                                            <TableCell className={[GU.tableCell, GU.tableCellRemove].join(' ')} align="center">
                                                                <Button
                                                                    className={GU.removeUserButton}
                                                                    onClick={() => removeUser(row.scheduleGroupUserId)}
                                                                >
                                                                    <Delete className={GU.removeUserIcon} />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </>
                        )}
                    </Grid>
                </>
            </SCUIContent>
        </>
    );
};
